import React, { type FunctionComponent } from "react";
import { Card, message, Space, Switch, Typography } from "antd";

import { programAutomationActions } from "@fitness-app/app-store";

import { useLoading } from "~/hooks/useLoading";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

// TRE-1603 — explicit "this is a low-ticket 2.0 automation" switch. When on, adding the automation
// to a client stamps trainee.metadata.flags.clientAdvisor, which unlocks the client-facing AI
// advisor (home tile, chat, proactive messages) on mobile + web + the chat endpoint. Off = the
// legacy flow, byte-for-byte unchanged. Offer touchpoints are NOT gated by this — they depend on
// the client's product + the offer's audience window (see the Oferty tab).
export const AdvisorAutomationToggle: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const selectedAutomation = useAppSelector((store) => store.programAutomation.selectedAutomation);
  const [saving, startSaving, stopSaving] = useLoading();

  if (!selectedAutomation) {
    return null;
  }

  const advisorEnabled = Boolean(selectedAutomation.advisorEnabled);
  // TRE-2184: default on; only an advisor automation may switch the trainer chat off.
  const trainerChatEnabled = selectedAutomation.trainerChatEnabled !== false;
  const welcomeMessages = selectedAutomation.hasActiveWelcomeMessages
    ? (selectedAutomation.welcomeMessages?.length ?? 0)
    : 0;

  const save = async (automation: { advisorEnabled?: boolean; trainerChatEnabled?: boolean }, done: string) => {
    startSaving();
    try {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({ automationId: selectedAutomation.id, automation }),
      ).unwrap();
      void message.success(done);
    } catch {
      void message.error("Nie udało się zapisać ustawienia Opiekunki");
    } finally {
      stopSaving();
    }
  };

  const onChange = (checked: boolean) =>
    save({ advisorEnabled: checked }, checked ? "Opiekunka włączona dla tej automatyzacji" : "Opiekunka wyłączona");

  const onTrainerChatChange = (checked: boolean) =>
    save(
      { trainerChatEnabled: checked },
      checked ? "Klientki dostaną też czat z trenerem" : "Klientki rozmawiają tylko z Opiekunką",
    );

  return (
    <Card size="small">
      <div className="flex items-center justify-between gap-4">
        <Space direction="vertical" size={2}>
          <Typography.Text strong>AI Opiekunka Metamorfozy (low-ticket 2.0)</Typography.Text>
          <Typography.Text type="secondary">
            Włączone = klientki dodane do tej automatyzacji dostają AI Opiekunkę (kafelek na home, czat, proaktywne
            wiadomości). Wyłączone = klasyczny flow bez Opiekunki, jak dotąd.
          </Typography.Text>
        </Space>
        <Switch checked={advisorEnabled} loading={saving} onChange={(checked) => void onChange(checked)} />
      </div>
      {advisorEnabled ? (
        <div className="mt-4 flex items-center justify-between gap-4 border-t border-gray-100 pt-4">
          <Space direction="vertical" size={2}>
            <Typography.Text strong>Czat z trenerem</Typography.Text>
            <Typography.Text type="secondary">
              Wyłączone = klientka nie dostaje kanału z trenerem, wszystko idzie przez Opiekunkę (jedna zakładka czatu).
              Zaplanowane wiadomości otwierają wtedy rozmowę z Opiekunką, a kanał powstaje dopiero po przejściu na
              produkt z trenerem.
            </Typography.Text>
            {!trainerChatEnabled && welcomeMessages > 0 ? (
              <Typography.Text type="warning">
                Ta automatyzacja ma {welcomeMessages} wiadomości powitalnych: bez czatu z trenerem nie zostaną wysłane.
                Zamiast nich dodaj wiadomość AI z nadawcą Opiekunka na dzień 1.
              </Typography.Text>
            ) : null}
          </Space>
          <Switch
            checked={trainerChatEnabled}
            loading={saving}
            onChange={(checked) => void onTrainerChatChange(checked)}
          />
        </div>
      ) : null}
    </Card>
  );
};

export default AdvisorAutomationToggle;
